import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

import { ICONS } from './icons';

export interface FieldMessagesProps {
  /** Id the control's `aria-describedby` points at. Rendered only when `description` is set. */
  descriptionId: string;
  description?: string;
  /** Id the control's `aria-describedby` points at. Rendered only when `error` is set. */
  errorId: string;
  error?: string;
  className?: string;
}

/**
 * Helper text and errors — the ONE implementation, so no field can grow its own dialect.
 *
 * SIZE, AND WHY IT CHANGED. These render at `--text-sm` (13px). They used to be `--text-2xs`
 * (11px). DESIGN.md §2's scale assigns 13px to "Metadata, tabellceller, hjelpetekst" —
 * hjelpetekst is exactly this — and the same section draws a hard line two paragraphs earlier:
 * "Aldri under 13 px for noe en bruker skal lese". 11px is reserved by name for badges and kbd
 * hints, "aldri en setning". A helper sentence is a sentence, and an error message is a
 * sentence read at 23:00 in the rain by someone the form has just rejected. It gets the
 * readable size, even though it costs two pixels of density — which the notched label above it
 * has already paid for several times over.
 *
 * THE ERROR IS ADDED, NOT SWAPPED IN. The description never disappears. Losing the instruction
 * at the exact moment you failed to follow it is the oldest bad habit in form design. Both are
 * pointed at by the control's `aria-describedby`, description first, so a screen reader hears
 * them in the order they are drawn.
 *
 * COLOUR IS NEVER THE SIGNAL (DESIGN.md §3, §11.2). The error carries an icon and words. Red on
 * the outline and red on the label are the third and fourth ways of saying it, not the first.
 *
 * INSET. Both align with the value the user typed — `--space-3`, the control's own inline
 * padding — so the sentence reads as belonging to the field above it rather than to the form.
 */
export function FieldMessages({
  descriptionId,
  description,
  errorId,
  error,
  className,
}: FieldMessagesProps): ReactNode {
  if (description === undefined && error === undefined) return null;
  const Alert = ICONS.alert;

  return (
    <div
      data-field-messages=""
      className={cn('flex flex-col gap-1 ps-3', className)}
      /*
        THE GAP IS THE TOKEN, NOT A NUMBER THAT AGREES WITH IT TODAY.
        --------------------------------------------------------------------------------------
        This said `mt-1.5` — 6px — under a comment explaining that 6px was the value that had
        already been found wrong. `--focus-ring-clearance` exists for exactly this distance and
        is 12px (extent + space-2), and its own comment in `globals.css` says every place that
        must stay clear of a ring reads THAT rather than a literal. This was the place that did
        not, and Atle saw the result: «I mange popups merker jeg at samme feil vi tok opp for noen
        dager siden skjer med helper text, og at linje legges over teksten.»

        The ring is LIFTED, so where it used to pass behind the sentence it now passes in front —
        which is why the arithmetic that said 6px was enough kept losing to somebody looking at
        the screen. In a style attribute rather than a class because the value is a custom
        property; `focus-ring.spec.ts` fails on a literal here.
      */
      style={{ marginTop: 'var(--focus-ring-clearance)' }}
    >
      {description !== undefined ? (
        <p id={descriptionId} className="text-sm leading-snug text-muted">
          {description}
        </p>
      ) : null}
      {error !== undefined ? (
        <p
          id={errorId}
          role="alert"
          className="flex items-start gap-1.5 text-sm leading-snug font-medium text-[color:var(--color-danger)]"
        >
          <Alert aria-hidden className="mt-0.5 size-3.5 shrink-0" />
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
}
